import React, { useState, useMemo } from 'react';
import { Box, Drawer, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close'; 
import TechActionStatGrid from './TechActionStatGrid';
import TechActionStatFilter from './TechActionStatFilter';
import SalesActionStatDetailCard from './SalesActionStatDetailCard';

const computeDetailedWorkMinutes = (startYear, startMonth, endYear, endMonth, holidayList = []) => {
  if (!startYear || !endYear || !startMonth || !endMonth) return { targetMinutes: 20 * 8 * 60, passedMinutes: 20 * 8 * 60 };

  const startDateObj = new Date(Number(startYear), startMonth - 1, 1);
  const endDateObj = new Date(Number(endYear), endMonth, 0); 
  const today = new Date();

  const todayDateObj = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const effectiveEnd = endDateObj > todayDateObj ? todayDateObj : endDateObj;

  let targetMinutes = 0;   
  let passedMinutes = 0;   

  let current = new Date(startDateObj);

  while (current <= endDateObj) {
    const dayOfWeek = current.getDay();
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      current.setDate(current.getDate() + 1);
      continue;
    }

    const daySetting = holidayList.find(item => item.holidayDate === dateString);
    let dayMinutes = 8 * 60; 

    if (daySetting) {
      if (daySetting.workType === 'FULL') dayMinutes = 0;
      else if (daySetting.workType === 'HALF') dayMinutes = 4 * 60;
    }

    targetMinutes += dayMinutes;

    if (current <= effectiveEnd) {
      passedMinutes += dayMinutes;
    }

    current.setDate(current.getDate() + 1);
  }

  return { targetMinutes, passedMinutes };
};

const TechActionStat = ({ 
    statEvents = [],
    actionEvents = [],
    holidayList = [],
    dptUuid,
    setDptUuid,
    usrUuid,
    setUsrUuid,
    dptList = [],
    usrList = [],
    onFetchStat
}) => {
    const now = new Date();
    const [startYear, setStartYear] = useState(now.getFullYear());
    const [startMonth, setStartMonth] = useState(now.getMonth() + 1);
    const [endYear, setEndYear] = useState(now.getFullYear());
    const [endMonth, setEndMonth] = useState(now.getMonth() + 1);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // ✨ [요구사항 반영]: 필터 컴포넌트에서 실시간 가공해 올려준 정보를 담을 State 추가
    const [activeFilterLabels, setActiveFilterLabels] = useState({ type: '', list: [] });

    const { targetWorkMinutes, baseWorkMinutes } = useMemo(() => {
        const { targetMinutes, passedMinutes } = computeDetailedWorkMinutes(startYear, startMonth, endYear, endMonth, holidayList);
        return { targetWorkMinutes: targetMinutes, baseWorkMinutes: passedMinutes };
    }, [startYear, startMonth, endYear, endMonth, holidayList]);

    const activeUserCount = useMemo(() => {
        if (usrUuid && typeof usrUuid === 'string') return 1;
        if (Array.isArray(usrUuid)) return usrUuid.length > 0 ? usrUuid.length : 1;
        if (!dptUuid) return usrList.length;
        const filteredCount = usrList.filter(usr => usr.dptUuid === dptUuid).length;
        return filteredCount > 0 ? filteredCount : 1;
    }, [dptUuid, usrUuid, usrList]);

    const handleRowClick = (rowData) => {
        setSelectedDetail(rowData);
        setIsDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedDetail(null);
    };

    // 🚀 [핵심 연결고리]: 하위 필터 컴포넌트의 콜백 가로채기 및 State 주입용 래퍼 함수
    const handleFilterFetchStat = (startDate, endDate, targetDptUuid, targetUsrUuid, targetLoginUuid, filterData) => {
        // 1. 하위 필터에서 가공되어 올라온 정보(type, list)를 부모 State에 저장
        if (filterData) {
            setActiveFilterLabels(filterData);
        }
        
        // 2. 상위(최상위 통신부)에서 내려받은 기존 API 요청부 실행 (순수 UUID 파라미터들만 전달)
        if (onFetchStat) {
            onFetchStat(startDate, endDate, targetDptUuid, targetUsrUuid, targetLoginUuid);
        }
    };

    return (
    <Box 
        sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            width: '100%', 
            height: '100%',
            p: 2,
            backgroundColor: '#ffffff',
        }}
    >
        <Box sx={{ width: '100%' }}>
            {/* 💡 래핑된 통신 함수인 handleFilterFetchStat를 주입합니다. */}
            <TechActionStatFilter 
                dptUuid={dptUuid} setDptUuid={setDptUuid}
                usrUuid={usrUuid} setUsrUuid={setUsrUuid}
                startYear={startYear} setStartYear={setStartYear}
                startMonth={startMonth} setStartMonth={setStartMonth}
                endYear={endYear} setEndYear={setEndYear}
                endMonth={endMonth} setEndMonth={setEndMonth}
                dptList={dptList}
                usrList={usrList}
                onFetchStat={handleFilterFetchStat}
            />

            {/* 💡 [그리드 연동]: 기존 전체 리스트 대신 필터링된 정보(activeFilterLabels)를 함께 바인딩해 줍니다. */}
            <TechActionStatGrid 
                statEvents={statEvents} 
                actionEvents={actionEvents}
                holidayList={holidayList}
                targetWorkMinutes={targetWorkMinutes}
                baseWorkMinutes={baseWorkMinutes} 
                startYear={startYear}
                startMonth={startMonth}
                endYear={endYear}
                endMonth={endMonth}
                userCount={activeUserCount} 
                onRowClick={handleRowClick}
                dptList={dptList}
                usrList={usrList}
                filterLabels={activeFilterLabels} // 추가된 Props
            /> 
        </Box>

        <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={handleCloseDrawer}
            variant="temporary"
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 600 },
                    p: 2,
                    boxSizing: 'border-box',
                    boxShadow: '-4px 0px 20px rgba(0,0,0,0.08)'
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <IconButton onClick={handleCloseDrawer} size="small" color="default">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {selectedDetail && (
                <SalesActionStatDetailCard selectedDetail={selectedDetail} />
            )}
        </Drawer>
    </Box>
);

};

export default TechActionStat;
